import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import _ from "lodash";

import { connect } from "react-redux";
import PActions from "../../Stores/redux/Persisted/Actions";
import UnpActions from "../../Stores/redux/Unpersisted/Actions";

import Modal from "../../Components/Modal/Modal";
import AutoCompleteDropdownStatic from "../../Components/Input/AutoCompleteDropdownStatic";
import api from "../../Services/Api/api";
import etcModule from "../../Modules/etc/etc-module";

const NewSchemeForm = ({
  isOpen,
  onClose,
  locationData,
  setScreenState,
  user,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [subdivisionOptions, setSubdivisionOptions] = useState(null);
  const [blockOptions, setBlockOptions] = useState(null);
  const [circleOptions, setCircleOptions] = useState(null);
  const [lacOptions, setLacOptions] = useState(null);
  const [officeOptions, setOfficeOptions] = useState(null);

  const [formData, setFormData] = useState({
    district: user.district._id,
    subdivision: null,
    block: null,
    circle: null,
    lac: null,
    name: "",
    details: "",
    office: null,
    lastDate: null,
    amount: null,
    creationDate: Date.now(),
  });

  const totalSteps = 2;

  useEffect(() => {
    if (isOpen && !locationData) {
      loadLocationData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (locationData) {
      setSubdivisionOptions([
        {
          items: locationData?.subdivisions?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setBlockOptions([
        {
          items: locationData?.blocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setCircleOptions([
        {
          items: locationData?.circles?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setLacOptions([
        {
          items: locationData?.lacs?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
    }
  }, [locationData]);

  useEffect(() => {
    if (step === 2) {
      resetOffices();
    }
  }, [step]);

  const resetOffices = async () => {
    setOfficeOptions(null);
    try {
      const { offices } = await api.get("v1/district-admin/office", {
        filter: JSON.stringify({
          where: _.pick(formData, [
            "district",
            "subdivision",
            "block",
            "circle",
            "lac",
          ]),
        }),
      });

      setOfficeOptions([
        {
          items: offices.map((x) => ({ value: x._id, label: x.name })),
        },
      ]);
    } catch (e) {
      toast.error("Error loading office list: " + e.message);
    }
  };

  const loadLocationData = async () => {
    try {
      const locationData = await api.get("v1/appdata/location-data", {
        district: user.district._id,
      });
      setScreenState(
        { locationData: { ...locationData, uid: Date.now() } },
        false,
        "APP"
      );
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  // Handle Next / Previous Steps
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  // Handle Input Change
  const handleChange = (name, value) =>
    setFormData({ ...formData, [name]: value });

  // Submit Form
  const handleSubmit = async () => {
    try {
      if (loading) return;

      setError(null);
      setLoading(true);

      const { scheme } = await api.post("v1/district-admin/scheme", formData);
      toast.success("Scheme created successfully");

      setLoading(false);
      onClose(); // Close modal after submit
      if (onSuccess) onSuccess(scheme);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      toast.error("Error: " + e.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Scheme"
      showFooter={false}
      closeOnOutsideClick={false}
    >
      {/* Step Indicator */}
      <div className="step-indicator">
        Step {step} of {totalSteps}
      </div>

      {/* Step 1 - Location Selection */}
      {step === 1 && (
        <>
          <div className="form-step">
            <label>District</label>
            <input type="text" value={user.district.name} disabled />

            <label>Subdivision</label>
            <AutoCompleteDropdownStatic
              options={subdivisionOptions}
              placeholder="Select Subdivision"
              value={formData.subdivision}
              onSelect={(value) => handleChange("subdivision", value)}
              loading={!subdivisionOptions}
            />

            <label>Block</label>
            <AutoCompleteDropdownStatic
              options={blockOptions}
              placeholder="Select Block"
              value={formData.block}
              onSelect={(value) => handleChange("block", value)}
              loading={!blockOptions}
            />

            <label>Circle</label>
            <AutoCompleteDropdownStatic
              options={circleOptions}
              placeholder="Select Circle"
              value={formData.circle}
              onSelect={(value) => handleChange("circle", value)}
              loading={!circleOptions}
            />

            <label>LAC</label>
            <AutoCompleteDropdownStatic
              options={lacOptions}
              placeholder="Select LAC"
              value={formData.lac}
              onSelect={(value) => handleChange("lac", value)}
              loading={!lacOptions}
            />
          </div>

          <div className="modal-footer">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            {formData.district &&
              formData.subdivision &&
              formData.block &&
              formData.circle &&
              formData.lac && (
                <button className="next-btn" onClick={nextStep}>
                  Next
                </button>
              )}
          </div>
        </>
      )}

      {/* Step 2 - Scheme Details */}
      {step === 2 && (
        <>
          <div className="form-step">
            <label>Scheme Name</label>
            <input
              type="text"
              placeholder="Enter Scheme Name"
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
            />

            <label>Details</label>
            <textarea
              placeholder="Enter Scheme Details"
              value={formData.details || ""}
              onChange={(e) => handleChange("details", e.target.value)}
            ></textarea>

            <label>Office</label>
            <AutoCompleteDropdownStatic
              options={officeOptions}
              loading={!officeOptions}
              placeholder="Select Office"
              value={formData.office || ""}
              onSelect={(value) => handleChange("office", value)}
            />

            <label>Last Date</label>
            <input
              type="date"
              value={formData.lastDate || ""}
              onChange={(e) => handleChange("lastDate", e.target.value)}
            />

            <div className="amount-input-container">
              <label>Amount</label>
              {formData.amount ? (
                <span className="amount-word">
                  {etcModule.formatNumber(parseFloat(formData.amount))}
                </span>
              ) : null}
            </div>
            <input
              type="number"
              placeholder="Enter Amount"
              value={formData.amount || ""}
              onChange={(e) => handleChange("amount", e.target.value)}
            />
          </div>

          <div className="modal-footer">
            <button className="prev-btn" onClick={prevStep}>
              Previous
            </button>
            {formData.name && formData.office && (
              <button className="submit-btn" onClick={handleSubmit}>
                {loading ? "Loading.." : "Submit"}
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  locationData: state.vState.APP.locationData,
  user: state.pState.AUTH.user,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = "SCHEMES_SCREEN") =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewSchemeForm);
